import React, { useEffect, useState, useMemo } from 'react';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Input, Label, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import CustomHeader from '../../../Components/Common/CustomHeader';
import type { CustomerState } from '../types/customer-states.types';
import { getCustomerStateTransitions, updateCustomerStateTransitions } from '../api/customer-states.api';
import CustomerStateBadge from './CustomerStateBadge';

type StateTransitionsConfiguratorProps = {
    isOpen: boolean;
    onClose: () => void;
    sourceState: CustomerState | null;
    allAvailableStates: CustomerState[];
};

export const StateTransitionsConfigurator: React.FC<StateTransitionsConfiguratorProps> = ({
    isOpen,
    onClose,
    sourceState,
    allAvailableStates,
}) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchTransitions = async () => {
            if (!isOpen || !sourceState) return;
            setIsFetching(true);
            try {
                const transitions = await getCustomerStateTransitions(sourceState.id);
                setSelectedIds(transitions || []);
            } catch (error: any) {
                console.error("Error al obtener transiciones:", error);
                toast.error("Error al cargar las transiciones configuradas");
            } finally {
                setIsFetching(false);
            }
        };

        fetchTransitions();
    }, [isOpen, sourceState]);

    // Exclude sourceState (self) and any INITIAL states from potential targets
    const filteredStates = useMemo(() => {
        if (!sourceState) return [];
        return allAvailableStates.filter(
            (state) => state.id !== sourceState.id && state.type !== 'INITIAL'
        );
    }, [allAvailableStates, sourceState]);

    const handleToggle = (stateId: string) => {
        setSelectedIds((prev) =>
            prev.includes(stateId)
                ? prev.filter((id) => id !== stateId)
                : [...prev, stateId]
        );
    };

    const handleSave = async () => {
        if (!sourceState) return;
        setIsSaving(true);
        try {
            await updateCustomerStateTransitions(sourceState.id, selectedIds);
            toast.success("Transiciones actualizadas correctamente");
            onClose();
        } catch (error: any) {
            console.error("Error al guardar transiciones:", error);
            toast.error(error.message || "Error al guardar las transiciones");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Offcanvas
            isOpen={isOpen}
            toggle={onClose}
            direction="end"
            className="d-flex flex-column"
        >
            <CustomHeader
                title={`Transiciones: ${sourceState?.name || ''}`}
                onClose={onClose}
                component={OffcanvasHeader}
            />

            <OffcanvasBody className="flex-grow-1">
                {isFetching ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="text-muted fs-13 mb-4 d-flex align-items-center gap-2 flex-wrap">
                            Seleccione los estados a los cuales se permite transicionar desde el estado <strong>{sourceState?.name}</strong>
                            {sourceState && <CustomerStateBadge state={{ type: sourceState.type }} />}
                        </p>

                        {filteredStates.length === 0 ? (
                            <div className="text-center text-muted my-5">
                                <i className="ri-information-line fs-24 mb-2 d-block"></i>
                                No hay otros estados disponibles para configurar.
                            </div>
                        ) : (
                            <div className="vstack gap-3">
                                {filteredStates.map((state) => {
                                    const isChecked = selectedIds.includes(state.id);
                                    return (
                                        <div
                                            key={state.id}
                                            className="d-flex align-items-center justify-content-between p-2 rounded bg-light-subtle border border-light"
                                        >
                                            <div className="flex-grow-1 me-2">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <h6 className="fs-13 mb-0 fw-semibold">{state.name}</h6>
                                                    <CustomerStateBadge state={{ type: state.type }} />
                                                </div>
                                                {state.description && (
                                                    <p className="text-muted fs-12 mb-0 text-truncate" style={{ maxWidth: '240px' }}>
                                                        {state.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="form-check form-switch form-switch-md" dir="ltr">
                                                <Input
                                                    type="checkbox"
                                                    id={`transition-switch-${state.id}`}
                                                    className="form-check-input"
                                                    checked={isChecked}
                                                    onChange={() => handleToggle(state.id)}
                                                    disabled={isSaving}
                                                />
                                                <Label
                                                    htmlFor={`transition-switch-${state.id}`}
                                                    className="form-check-label visually-hidden"
                                                >
                                                    Permitir transición a {state.name}
                                                </Label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </OffcanvasBody>

            <div className="p-3 border-top bg-light">
                <div className="d-flex gap-2">
                    <Button
                        color="light"
                        onClick={onClose}
                        className="w-100"
                        disabled={isSaving}
                    >
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSave}
                        className="w-100"
                        disabled={isFetching || isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Guardando...
                            </>
                        ) : (
                            'Guardar'
                        )}
                    </Button>
                </div>
            </div>
        </Offcanvas>
    );
};

export default StateTransitionsConfigurator;
